import { useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { Outlet, useNavigate, useParams } from 'react-router-dom';

import SidebarLayout from 'layouts/SidebarLayout';
import { SidebarLayoutContent } from 'layouts/SidebarLayout';
import { SidebarLayoutNavigation } from 'layouts/SidebarLayout';

import ErrorMessage from 'components/ErrorMessage';

import LoadingIndicator from 'primitives/LoadingIndicator';

import informationIcon from 'media/icons/information.svg';
import moneyIcon from 'media/icons/investment.svg';
import manageIcon from 'media/icons/manage.svg';

const DEAL_QUERY = gql(`
  query GetDealForFund($id: ID!) {
    deal(id: $id) {
      id
      status
      companyInformation {
        id
        company {
          id
          name
        }
      }
      type
    }
  }
`);

const GET_FUND_SYNDICATE = gql(`
  query Fund($fundIdFor: ID!) {
    fund(id: $fundIdFor) {
      id
      syndicate {
        id
      }
    }
  }
`);

const FundDealPage = () => {
  const navigate = useNavigate();
  const { fundId, dealId } = useParams<{ fundId: string; dealId: string }>() as {
    fundId: string;
    dealId: string;
  };

  const { data: fundData, loading: fundLoading } = useQuery(GET_FUND_SYNDICATE, {
    variables: {
      fundIdFor: fundId,
    },
  });

  const syndicateId = fundData?.fund.syndicate?.id || '';

  const { loading, error, data, refetch } = useQuery(DEAL_QUERY, {
    variables: {
      id: dealId,
    },
  });

  const renderContent = () => {
    if (loading || fundLoading) return <LoadingIndicator />;

    if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

    const deal = data.deal;

    if (deal.status === 'DRAFT') {
      navigate(`/fund-dashboard/${syndicateId}/create-deal/${dealId}/basic-information`, {
        replace: true,
      });
    }

    const tabs = [
      {
        key: 'manage',
        label: 'Manage',
        description: 'Manage your deal',
        route: `/fund-dashboard/${fundId}/deals-of-fund/${dealId}/manage`,
        icon: manageIcon,
      },
      {
        key: 'consent-invite',
        label: 'Consent Invites',
        description: 'Manage your consent invites',
        route: `/fund-dashboard/${fundId}/deals-of-fund/${dealId}/consent-invite`,
        icon: manageIcon,
      },
      {
        key: 'commitments',
        label: 'Commitments',
        description: 'View commitments for this deal',
        route: `/fund-dashboard/${fundId}/deals-of-fund/${dealId}/commitments`,
        icon: moneyIcon,
      },
      {
        key: 'information',
        label: 'Information',
        description: 'View your deal information',
        route: `/fund-dashboard/${fundId}/deals-of-fund/${dealId}/information`,
        icon: informationIcon,
      },
    ];

    return (
      <>
        <SidebarLayoutNavigation
          onBackButtonClick={() => navigate(-1)}
          title={deal.companyInformation.company.name}
          subTitle={'Fund Deal'}
        />
        <SidebarLayoutContent secondaryTabs={tabs}>
          <Outlet />
        </SidebarLayoutContent>
      </>
    );
  };

  return <SidebarLayout>{renderContent()}</SidebarLayout>;
};

export default FundDealPage;
